import {
  Box,
  Grid,
  Icon,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@ace-clublabs/shared-component-library';
import {
  LocalizationProvider,
  MobileDatePicker,
  type MobileDatePickerProps,
  StaticDatePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import TimeSlot from '@/features/appointments/components/TimeSlot';
import type { TimeSlot as TimeSlotInterface } from '@/features/appointments/utils/TimeSlot';
import useScreenSize from '@/hooks/useScreenSize';

type DateTimePickerProps = {
  timeSlots: TimeSlotInterface[];
  selectedDate: Date | undefined;
  selectedTimeSlot: TimeSlotInterface | null;
  onChangeDate: (date: Date) => void;
  onChangeTimeSlot: (timeSlot: TimeSlotInterface | null) => void;
  disabled?: boolean;
  isLoading?: boolean;
};

const getGenericTimeZoneName = () => {
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'longGeneric',
    });
  } catch (_error) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'long',
    });
  }

  const formatted = formatter.format();
  const zoneName = formatted.split(',')[1].trim();
  return zoneName;
};

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  timeSlots,
  selectedDate,
  selectedTimeSlot,
  onChangeDate,
  onChangeTimeSlot,
  disabled = false,
  isLoading = false,
}) => {
  const filteredDates = new Set(
    timeSlots.map((timeSlot) => dayjs(timeSlot.startTime).format('MM/DD/YYYY')),
  );

  const filteredSlots = timeSlots.filter(
    (timeSlot) =>
      dayjs(timeSlot.startTime).format('MM/DD/YYYY') ===
      dayjs(selectedDate).format('MM/DD/YYYY'),
  );

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const isTabletView = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { width } = useScreenSize();

  const calendarWidth = 368;
  const availableWidth = width - 16; // 16 px for padding
  const calendarScale = Math.min(1, availableWidth / calendarWidth);

  const timeZoneName = useMemo(() => getGenericTimeZoneName(), []);

  const handleDateSelected = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    onChangeDate(date.toDate());
  };

  const handleTimeSlotSelected = (timeSlot: TimeSlotInterface) => {
    onChangeTimeSlot(timeSlot);
  };

  const handleShouldDisableDate = (day: dayjs.Dayjs) => {
    return !filteredDates.has(day.format('MM/DD/YYYY'));
  };

  const datePickerProps: MobileDatePickerProps<dayjs.Dayjs> = {
    maxDate: dayjs().add(30, 'day'),
    reduceAnimations: true,
    disableHighlightToday: true,
    disablePast: true,
    disabled,
    value: disabled || !selectedDate ? null : dayjs(selectedDate),
    onChange: handleDateSelected,
    shouldDisableDate: handleShouldDisableDate,
    format: 'MM / DD / YYYY',
    slotProps: {
      dialog: {
        sx: (theme) => ({
          '& .MuiPaper-root': {
            m: 0,
            p: '0 !important',
            overflowY: 'unset',
            width: `${calendarWidth}px !important`,
            minWidth: `${calendarWidth}px !important`,
            [theme.breakpoints.down('sm')]: {
              transform: `scale(${calendarScale})`,
              transformOrigin: 'center',
            },
          },
        }),
      },
      layout: {
        sx: {
          boxShadow: '0px 2px 4px 0px rgba(8, 17, 34, 0.2)',
          borderRadius: '6px',
          '& .MuiPickersLayout-contentWrapper': {
            width: 'auto !important',
          },
          '& .MuiPickersToolbar-root,.MuiDialogActions-root,.MuiPickersCalendarHeader-switchViewButton':
            {
              display: 'none',
            },
          '& .MuiDateCalendar-root': {
            height: 'auto',
            minHeight: 'auto',
            mb: 0,
          },
          '& .MuiPickersSlideTransition-root': {
            minHeight: 'auto !important',
          },
          '& .MuiPickersDay-root': {
            '&:focus.Mui-selected': {
              backgroundColor: 'primary.600',
            },
            '&:hover:not(:disabled):not(.Mui-selected)': {
              color: 'inherit',
            },
          },
          '& .MuiButtonBase-root:disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
          },
        },
      },
      textField: {
        fullWidth: true,
        sx: {
          '& .MuiInputBase-root': {
            overflow: 'hidden',
          },
          '& .MuiOutlinedInput-root.MuiInputBase-readOnly:not(.Mui-visuallyEnabled)':
            {
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'primary.100',
                '& fieldset': {
                  borderColor: 'grey.300',
                },
              },
            },
        },
        InputProps: {
          endAdornment: (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'inherit',
              }}
            >
              <Icon iconName='calendar-regular' standalone />
            </Box>
          ),
        },
      },
    },
  };

  return (
    <Stack
      spacing={{
        xs: 5,
        lg: 3,
      }}
      direction={{
        xs: 'column',
        lg: 'row',
      }}
      sx={{
        pb: '20px',
        height: '100%',
        width: '100%',
        alignItems: {
          xs: 'center',
          lg: 'flex-start',
        },
        justifyContent: {
          xs: 'flex-start',
          lg: 'center',
        },
      }}
    >
      {(isMobileView || isTabletView) && isLoading ? (
        <MobileDatePickerSkeleton />
      ) : (
        <>
          <Stack
            data-testid='datePicker'
            direction='column'
            sx={{
              alignItems: 'center',
              width: '100%',
              maxWidth: calendarWidth,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {isMobileView ? (
                <MobileDatePicker {...datePickerProps} />
              ) : (
                <StaticDatePicker {...datePickerProps} />
              )}
            </LocalizationProvider>
            {!isMobileView && (
              <Stack
                direction='column'
                sx={{
                  mt: 2,
                  width: '100%',
                }}
              >
                <Typography variant='b1Bold' sx={{ textAlign: 'left' }}>
                  Time zone
                </Typography>
                <Typography variant='b2Regular' sx={{ textAlign: 'left' }}>
                  {timeZoneName}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Grid
            container
            data-testid={
              isLoading || !timeSlots.length
                ? 'timeSlotContainerLoading'
                : 'timeSlotContainer'
            }
            sx={{
              px: 1,
              width: {
                xs: '100%',
                lg: 295,
              },
              maxWidth: { xs: calendarWidth, lg: '100%' },
              overflowY: 'scroll',
              maxHeight: {
                lg: 407,
                xs: 212,
              },
              scrollbarColor: 'grey transparent',
              flex: {
                xs: 1,
                lg: 'unset',
              },
              minHeight: {
                xs: 0,
                lg: 'auto',
              },
              alignContent: 'flex-start',
            }}
            columnSpacing={2}
            rowSpacing={{ xs: 2, lg: 1 }}
          >
            {filteredSlots.map((timeSlot: TimeSlotInterface) => (
              <Grid
                size={{
                  xs: 6,
                  lg: 12,
                }}
                key={timeSlot.startTime}
              >
                <TimeSlot
                  testId={timeSlot.startTime}
                  onClickHandler={() => handleTimeSlotSelected(timeSlot)}
                  label={dayjs(timeSlot.startTime).format('hh:mma').toString()}
                  isSelected={
                    selectedTimeSlot?.startTime === timeSlot.startTime
                  }
                  disabled={disabled}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Stack>
  );
};

const MobileDatePickerSkeleton = () => {
  return (
    <Stack
      direction='column'
      spacing={3}
      sx={{
        width: '100%',
        maxWidth: 368,
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
      }}
      data-testid='mobileDatePickerLoading'
    >
      <Stack
        direction='column'
        spacing={1}
        sx={{
          width: '100%',
        }}
      >
        <Skeleton
          variant='rounded'
          width='100%'
          sx={{
            height: {
              xs: '48px !important',
              md: '364px !important',
            },
          }}
        />
      </Stack>
      <Grid
        container
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflowY: 'scroll',
          justifyContent: 'flex-start',
          alignContent: 'flex-start',
        }}
        columnSpacing={2}
        rowSpacing={2}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <Grid
            // biome-ignore lint/suspicious/noArrayIndexKey: only available key
            key={index}
            size={6}
          >
            <Skeleton variant='rounded' width={'100%'} height={60} />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default DateTimePicker;
